type MaybeAuthError =
  | { message?: string | null; code?: string | null }
  | Error
  | null
  | undefined;

function isObjectLike(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function normalizeErrorParts(error: MaybeAuthError): { message: string; code: string } {
  const messageValue =
    isObjectLike(error) && typeof error.message === "string" ? error.message : "";
  const codeValue = isObjectLike(error) && typeof error.code === "string" ? error.code : "";
  const message = messageValue.toLowerCase();
  const code = codeValue.toLowerCase();
  return { message, code };
}

function getNumericStatus(error: MaybeAuthError): number | null {
  if (!isObjectLike(error)) {
    return null;
  }

  const statusValue = (error as Record<string, unknown>)["status"];
  return typeof statusValue === "number" ? statusValue : null;
}

export function isRefreshTokenRaceError(error: MaybeAuthError): boolean {
  const { message, code } = normalizeErrorParts(error);
  return (
    code.includes("refresh_token_already_used") ||
    message.includes("refresh_token_already_used") ||
    message.includes("invalid refresh token: already used") ||
    (message.includes("already used") && message.includes("refresh token"))
  );
}

export function isDefinitiveInvalidRefreshTokenError(error: MaybeAuthError): boolean {
  const { message, code } = normalizeErrorParts(error);
  if (isRefreshTokenRaceError(error)) {
    return false;
  }

  return (
    code.includes("invalid_grant") ||
    code.includes("session_not_found") ||
    code.includes("refresh_token_not_found") ||
    message.includes("invalid refresh token") ||
    message.includes("refresh token not found") ||
    message.includes("refresh token has been revoked") ||
    message.includes("token has been revoked") ||
    message.includes("refresh token revoked") ||
    message.includes("refresh token expired") ||
    message.includes("session_expired") ||
    (message.includes("jwt expired") && message.includes("refresh"))
  );
}

export function isRefreshRateLimitError(error: MaybeAuthError): boolean {
  const { message, code } = normalizeErrorParts(error);
  const status = getNumericStatus(error);

  return (
    status === 429 ||
    code.includes("over_request_rate_limit") ||
    code.includes("too_many_requests") ||
    message.includes("429") ||
    message.includes("rate limit") ||
    message.includes("too many requests")
  );
}

/**
 * 400 Bad Request from auth/token endpoint is terminal - invalid token, bad request, etc.
 * Do NOT retry - treat as definitive failure.
 */
export function isBadRequestError(error: MaybeAuthError): boolean {
  const status = getNumericStatus(error);
  return status === 400;
}

export function isTransientRefreshError(error: MaybeAuthError): boolean {
  if (isDefinitiveInvalidRefreshTokenError(error)) {
    return false;
  }

  if (isRefreshRateLimitError(error)) {
    return true;
  }

  if (isBadRequestError(error)) {
    return false;
  }

  const { message } = normalizeErrorParts(error);
  const status = getNumericStatus(error);

  return (
    (typeof status === "number" && status >= 500 && status <= 599) ||
    message.includes("network") ||
    message.includes("timeout") ||
    message.includes("timed out") ||
    message.includes("failed to fetch") ||
    message.includes("fetch failed") ||
    message.includes("temporary")
  );
}

/** True when supabase.auth.getUser() likely failed due to network / overload — not a definitive missing user. */
export function isTransientAuthUserFetchError(error: MaybeAuthError): boolean {
  if (!error) {
    return false;
  }
  if (isRefreshRateLimitError(error)) {
    return true;
  }
  const status = getNumericStatus(error);
  if (typeof status === "number" && status >= 500 && status <= 599) {
    return true;
  }
  const { message } = normalizeErrorParts(error);
  return (
    message.includes("fetch") ||
    message.includes("network") ||
    message.includes("failed to fetch") ||
    message.includes("timeout") ||
    message.includes("timed out") ||
    message.includes("load failed")
  );
}
